import React from 'react';
import AdSense from './AdSense';
import { Box, BoxProps } from '@mui/material';

interface AdSenseBannerProps extends Omit<BoxProps, 'className' | 'style'> {
  /**
   * Your AdSense ad slot ID
   */
  adSlot: string;
  /**
   * Vertical spacing (default: 2)
   */
  spacing?: number;
  /**
   * Optional key to force ad reload when changed (e.g., game ID)
   */
  reloadKey?: string | number;
}

/**
 * Pre-configured horizontal banner ad component
 * 
 * Usage:
 * ```tsx
 * <AdSenseBanner adSlot="1234567890" />
 * ```
 */
export const AdSenseBanner: React.FC<AdSenseBannerProps> = ({
  adSlot,
  spacing = 2,
  reloadKey,
  ...boxProps
}) => {
  return (
    <Box
      {...boxProps}
      sx={{
        width: '100%',
        my: spacing,
        display: 'flex',
        justifyContent: 'center',
        ...boxProps.sx,
      }}
    >
      <AdSense
        adSlot={adSlot}
        adFormat="horizontal"
        fullWidthResponsive
        reloadKey={reloadKey}
        style={{ width: '100%', maxWidth: '728px' }}
      />
    </Box>
  );
};

/**
 * Pre-configured vertical sidebar ad component
 * 
 * Usage:
 * ```tsx
 * <AdSenseSidebar adSlot="1234567890" />
 * ```
 */
export const AdSenseSidebar: React.FC<AdSenseBannerProps> = ({
  adSlot,
  spacing = 2,
  ...boxProps
}) => {
  return (
    <Box
      {...boxProps}
      sx={{
        width: '100%',
        my: spacing,
        display: 'flex',
        justifyContent: 'center',
        ...boxProps.sx,
      }}
    >
      <AdSense
        adSlot={adSlot}
        adFormat="vertical"
        fullWidthResponsive
        style={{ width: '100%', maxWidth: '300px' }}
      />
    </Box>
  );
};

/**
 * Pre-configured in-article ad component
 * 
 * Usage:
 * ```tsx
 * <AdSenseInArticle adSlot="1234567890" />
 * ```
 */
export const AdSenseInArticle: React.FC<AdSenseBannerProps> = ({
  adSlot,
  spacing = 2,
  ...boxProps
}) => {
  return (
    <Box
      {...boxProps}
      sx={{
        width: '100%',
        my: spacing,
        display: 'flex',
        justifyContent: 'center',
        ...boxProps.sx,
      }}
    >
      <AdSense
        adSlot={adSlot}
        adFormat="fluid"
        adLayout="in-article"
        fullWidthResponsive
        style={{ width: '100%' }}
      />
    </Box>
  );
};

